import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Quote, Plus, Minus, FlaskConical, Dumbbell, HeartPulse, BrainCircuit } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { Footer } from "../components/Footer";
import api from "../lib/api";
import { reveal3d, Tilt3D } from "../components/Reveal3D";
import { pick, catLabel } from "../lib/pick";
import useSeo from "../hooks/useSeo";
import { DiplomaCard } from "../components/DiplomaCard";

const fadeUp = reveal3d;

const COVER = (k) => `${process.env.REACT_APP_BACKEND_URL}/api/assets/covers/${k}.png`;

const IMG = {
  physiology: "https://static.prod-images.emergentagent.com/jobs/81851799-6536-43f7-96e8-5b14003d3dbf/images/5f92f063459380c35ab9e08ffe6275076fbb89da1fd811041688804b4d1eac51.jpeg",
  nutrition: "https://static.prod-images.emergentagent.com/jobs/81851799-6536-43f7-96e8-5b14003d3dbf/images/bf7f93890e5c61178efb027748ac8aaefb726104656415656f5e87326cbedaea.jpeg",
  strength: "https://static.prod-images.emergentagent.com/jobs/81851799-6536-43f7-96e8-5b14003d3dbf/images/24f6c2a66e8a9fd9614aadba8fbd3d60dd4471e075f3c1155eac4be82d08029f.jpeg",
  biomechanics: "https://static.prod-images.emergentagent.com/jobs/81851799-6536-43f7-96e8-5b14003d3dbf/images/541a280e669736e61fadfa0f680ace928545537d984787c43ced81278ff9eafc.jpeg",
  psychology: "https://static.prod-images.emergentagent.com/jobs/81851799-6536-43f7-96e8-5b14003d3dbf/images/ffd6c3dba91999f0a574efa268d7cf00f902afc5b4de5a640a0f4afb238ff88a.jpeg",
  stickerCoach: "https://static.prod-images.emergentagent.com/jobs/81851799-6536-43f7-96e8-5b14003d3dbf/images/abcea23ce5c53f0324d520928dcb6110777e4ba7bb782722242453e6ab28db2d.jpeg",
  stickerCert: "https://static.prod-images.emergentagent.com/jobs/81851799-6536-43f7-96e8-5b14003d3dbf/images/acbec862e8aa4db861f000135a4842c7b5787b9312fbdd3cac3b9e0897941df9.jpeg",
  runner: "https://images.unsplash.com/photo-1526676317768-d9b14f15615a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxzcG9ydHMlMjBjb2FjaCUyMGxhYm9yYXRvcnklMjB0ZXN0aW5nJTIwYXRobGV0ZXxlbnwwfHx8fDE3ODgxNzgyMTJ8MA&ixlib=rb-4.1.0&q=85",
  coaching: "https://images.unsplash.com/photo-1758875569256-f37c438cac65?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHw0fHxzcG9ydHMlMjBjb2FjaCUyMGxhYm9yYXRvcnklMjB0ZXN0aW5nJTIwYXRobGV0ZXxlbnwwfHx8fDE3ODgxNzgyMTJ8MA&ixlib=rb-4.1.0&q=85",
};

const Eyebrow = ({ children, dark }) => (
  <span className={dark ? "eyebrow-dark" : "eyebrow"}>
    <span className="w-7 h-px bg-[#1D4ED8]" />
    {children}
  </span>
);

const FaqItem = ({ q, a, index }) => {
  const [open, setOpen] = useState(index === 0);
  return (
    <div className="border-b border-[#E2E8F0]" data-testid={`faq-item-${index}`}>
      <button
        data-testid={`faq-toggle-${index}`}
        onClick={() => setOpen(!open)}
        className="w-full flex items-start justify-between gap-6 py-7 text-left group"
      >
        <span className={`font-heading font-black text-xl md:text-2xl transition-colors duration-200 ${open ? "text-[#1D4ED8]" : "text-[#0B1B3A] group-hover:text-[#1D4ED8]"}`}>
          {q}
        </span>
        <span className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition-colors duration-200 ${open ? "bg-[#1D4ED8] text-white" : "bg-[#EEF4FF] text-[#1D4ED8]"}`}>
          {open ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
        </span>
      </button>
      {open && (
        <motion.p
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
          className="text-[#475569] leading-relaxed pb-7 max-w-3xl"
          data-testid={`faq-answer-${index}`}
        >
          {a}
        </motion.p>
      )}
    </div>
  );
};

export default function Landing() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [online, setOnline] = useState([]);
  const [academy, setAcademy] = useState([]);
  const [pub, setPub] = useState(null);
  const [diplomas, setDiplomas] = useState([]);

  useSeo({
    lang,
    title:
      lang === "en"
        ? "SportScience Academy — Online Sports Science Courses & Certification"
        : "SportScience Academy — Online Μαθήματα Αθλητικής Επιστήμης & Πιστοποίηση",
    description:
      lang === "en"
        ? "Learn sports science online: coaching, physiology, nutrition, rehab and fitness business. Self-paced courses with quizzes, games and verifiable certificates."
        : "Μάθε αθλητική επιστήμη online: προπονητική, φυσιολογία, διατροφή, αποκατάσταση και fitness business. Μαθήματα με δικό σου ρυθμό, quiz, παιχνίδια και πιστοποιητικά.",
  });

  useEffect(() => {
    api.get("/courses").then((r) => setCourses(r.data)).catch(() => {});
    api.get("/public/stats").then((r) => setPub(r.data)).catch(() => {});
    api.get("/academy/courses").then((r) => {
      setAcademy(r.data);
      setOnline(r.data.filter((c) => !c.is_free && c.lessons_count > 0).slice(0, 6));
    }).catch(() => {});
    api.get("/courses").then((r) => setDiplomas(r.data.filter((c) => c.mode === "live"))).catch(() => {});
  }, []);

  const popular = academy
    .filter((c) => c.lessons_count > 0 && !online.some((o) => o.id === c.id))
    .sort((a, b) => (b.lessons_count || 0) - (a.lessons_count || 0))
    .slice(0, 3);

  const categories = [
    { key: "business", label: t("cat.business"), img: COVER("cat-business"), to: "/courses?category=Business%20του%20Γυμναστή" },
    { key: "training", label: t("cat.strength"), img: COVER("cat-training"), to: "/courses?category=Προπονητική" },
    { key: "physiology", label: t("cat.physiology"), img: COVER("cat-physiology"), to: "/courses?category=Φυσιολογία%20%26%20Προπόνηση" },
    { key: "populations", label: t("cat.populations"), img: COVER("cat-populations"), to: "/courses?category=Ειδικοί%20Πληθυσμοί" },
    { key: "wellbeing", label: t("cat.wellbeing"), img: COVER("cat-wellbeing"), to: "/courses?category=Wellbeing" },
    { key: "sports", label: t("cat.sports"), img: COVER("cat-sports"), to: "/courses?category=Ποδόσφαιρο" },
  ];

  const steps = [1, 2, 3, 4].map((n) => ({ n, title: t(`step${n}.title`), desc: t(`step${n}.desc`) }));

  const stats = [
    { v: pub ? `${pub.live_courses + pub.online_courses}+` : "…", l: t("stats.courses") },
    { v: pub ? `${pub.lessons}+` : "…", l: t("stats.lessons") },
    { v: pub ? `${pub.certificates}` : "…", l: t("stats.certs") },
  ];

  const why = [1, 2, 3, 4, 5].map((n) => ({ title: t(`why${n}.title`), desc: t(`why${n}.desc`) }));
  const [whyIdx, setWhyIdx] = useState(0);

  const faqs = [1, 2, 3, 4, 5].map((n) => ({ q: t(`faq${n}.q`), a: t(`faq${n}.a`) }));

  return (
    <div data-testid="landing-page" className="pb-10">
      {/* ---------- HERO ---------- */}
      <section className="max-w-7xl mx-auto px-5 sm:px-6 pt-6 md:pt-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="panel-cream overflow-hidden relative"
        >
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 p-7 sm:p-12 lg:p-16">
            <div className="lg:col-span-7">
              <Eyebrow dark>{t("hero.eyebrow")}</Eyebrow>
              <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-7 space-y-2 flex flex-col items-start gap-2" data-testid="hero-title">
                <span className="hl-lime py-1">{t("hero.block1")}</span>
                <span className="hl-ink py-1">{t("hero.block2")}</span>
                <span className="hl-cream py-1">{t("hero.block3")}</span>
              </h1>
              <p className="text-[#475569] text-base md:text-lg mt-8 max-w-lg" data-testid="hero-subtitle">
                {t("hero.tag")}
              </p>
              <div className="flex flex-wrap gap-3 mt-9">
                <Link data-testid="hero-cta-primary" to={user ? "/courses" : "/register"} className="btn-blue">
                  {t("hero.cta")} <ArrowRight className="w-4 h-4" />
                </Link>
                <Link data-testid="hero-cta-secondary" to="/courses" className="btn-ghost-dark">
                  {t("hero.cta2")}
                </Link>
              </div>
            </div>

            {/* Sticker deck */}
            <div className="lg:col-span-5 relative min-h-[320px] sm:min-h-[420px]">
              <motion.img
                data-testid="hero-image"
                src={IMG.stickerCoach}
                alt="Coaching tools"
                initial={{ opacity: 0, rotate: -14, y: 30 }}
                animate={{ opacity: 1, rotate: -9, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="absolute left-0 sm:left-4 top-2 w-[46%] rounded-[22px] border-2 border-[#1D4ED8] shadow-[8px_8px_0_0_#1D4ED8]"
              />
              <motion.img
                src={IMG.runner}
                alt="Athlete on blocks"
                initial={{ opacity: 0, rotate: 12, y: 30 }}
                animate={{ opacity: 1, rotate: 7, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="absolute right-0 top-16 w-[52%] h-[260px] sm:h-[320px] object-cover rounded-[22px] border-2 border-[#1D4ED8] shadow-[8px_8px_0_0_#1D4ED8]"
              />
              <motion.img
                src={IMG.stickerCert}
                alt="Certificate"
                initial={{ opacity: 0, rotate: -4, y: 40 }}
                animate={{ opacity: 1, rotate: -2, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="absolute left-[18%] bottom-0 w-[42%] rounded-[22px] border-2 border-[#1D4ED8] shadow-[8px_8px_0_0_#1D4ED8]"
              />
            </div>
          </div>
        </motion.div>
      </section>

      {/* ---------- LIVE DIPLOMAS ---------- */}
      {diplomas.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-6 pt-16 md:pt-20 pb-20 md:pb-28" data-testid="landing-diplomas-section">
          <motion.div {...fadeUp} className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div className="max-w-2xl">
              <Eyebrow>{t("live.badge")}</Eyebrow>
              <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl mt-5 text-[#0B1B3A]" data-testid="landing-diplomas-title">
                {t("landing.diplomas.title")}
              </h2>
              <p className="text-[#475569] mt-4">{t("diplomas.subtitle")}</p>
              <p className="text-[#1D4ED8] font-bold mt-3" data-testid="landing-diplomas-async">{t("live.asyncShort")}</p>
            </div>
            <Link to="/diplomas" className="btn-outline-light inline-flex shrink-0" data-testid="landing-diplomas-all">
              {t("landing.diplomas.all")} <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-12">
            {diplomas.map((p, i) => (
              <DiplomaCard key={p.id} program={p} delay={Math.min(i, 3) * 0.08} />
            ))}
          </div>
        </section>
      )}

      {/* ---------- CATEGORIES ---------- */}
      <section className="section" data-testid="categories-section">
        <motion.div {...fadeUp} className="text-center flex flex-col items-center">
          <Eyebrow>{t("cats.eyebrow")}</Eyebrow>
          <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl mt-5 text-[#0B1B3A]" data-testid="categories-title">
            {t("cats.title")} <span className="text-[#1D4ED8]">{t("cats.title2")}</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mt-14">
          {categories.map((c, i) => (
            <motion.div key={c.key} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.07 }}>
              <Link data-testid={`category-card-${c.key}`} to={c.to} className="block group">
                <div className="bg-white rounded-[24px] p-3 border border-[#E2E8F0] group-hover:border-[#1D4ED8] group-hover:-translate-y-2 transition-all duration-300 shadow-[0_18px_40px_-24px_rgba(29,78,216,0.45)]">
                  <img src={c.img} alt={c.label} className="w-full aspect-square object-cover rounded-[16px] bg-[#EEF4FF]" />
                  <div className="mt-3 text-center font-heading font-black text-sm text-[#0B1B3A] group-hover:text-[#1D4ED8] transition-colors duration-200 leading-tight min-h-[36px] flex items-center justify-center">
                    {c.label}
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ---------- WHY US (Workearly-style tabs) ---------- */}
      <section className="max-w-7xl mx-auto px-5 sm:px-6 pb-20 md:pb-28" data-testid="why-section">
        <motion.div {...fadeUp} className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-4">
            <Eyebrow>{t("why.eyebrow")}</Eyebrow>
            <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl mt-5 text-[#0B1B3A]" data-testid="why-title">{t("why.title")}</h2>
            <div className="mt-8 flex flex-col gap-1">
              {why.map((w, i) => (
                <button
                  key={i}
                  data-testid={`why-tab-${i}`}
                  onMouseEnter={() => setWhyIdx(i)}
                  onClick={() => setWhyIdx(i)}
                  className={`text-left px-5 py-3.5 rounded-2xl font-heading font-black text-base md:text-lg transition-colors duration-200 ${whyIdx === i ? "bg-[#1D4ED8] text-white" : "text-[#334155] hover:bg-[#EEF4FF]"}`}
                >
                  {w.title}
                </button>
              ))}
            </div>
          </div>
          <motion.div key={whyIdx} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} className="lg:col-span-8 card-dark p-8 sm:p-12 grid grid-cols-1 md:grid-cols-2 gap-8 items-center" data-testid="why-panel">
            <div>
              <h3 className="font-heading font-black text-2xl sm:text-3xl text-[#0B1B3A]">{why[whyIdx].title}</h3>
              <p className="text-[#475569] mt-4 leading-relaxed">{why[whyIdx].desc}</p>
              <Link to="/courses" className="btn-blue mt-7 inline-flex" data-testid="why-cta">{t("hero.cta2")} <ArrowRight className="w-4 h-4" /></Link>
            </div>
            <img src={COVER(["coach-cheer", "cat-training", "logo", "cat-business", "cat-wellbeing"][whyIdx])} alt="" className="w-full max-h-[300px] object-contain rounded-[22px]" />
          </motion.div>
        </motion.div>
      </section>

      {/* ---------- HOW IT WORKS ---------- */}
      <section className="max-w-7xl mx-auto px-5 sm:px-6" data-testid="steps-section">
        <Tilt3D strength={8}>
        <motion.div {...fadeUp} className="panel-lime p-8 sm:p-12 lg:p-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <Eyebrow dark>{t("steps.eyebrow")}</Eyebrow>
              <h2 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.02] mt-6 text-[#0B1B3A]" data-testid="steps-title">
                {t("steps.title")}
                <br />
                <span className="italic">{t("steps.title2")}</span>
              </h2>
              <p className="text-[#475569] mt-6 max-w-sm">{t("steps.subtitle")}</p>
              <Link data-testid="steps-cta" to="/courses" className="btn-dark mt-8">
                {t("hero.cta2")} <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="lg:col-span-7 relative">
              <div className="absolute left-[18px] top-3 bottom-3 border-l-2 border-dashed border-[#1D4ED8]/30 hidden sm:block" />
              <div className="space-y-9">
                {steps.map((s, i) => (
                  <motion.div key={s.n} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.08 }} className="flex gap-6 relative" data-testid={`step-${s.n}`}>
                    <span className="shrink-0 w-10 h-10 rounded-full bg-[#1D4ED8] text-white font-heading font-black flex items-center justify-center relative z-10">
                      {s.n}
                    </span>
                    <div>
                      <h3 className="font-heading font-black text-xl md:text-2xl text-[#0B1B3A]">{s.title}</h3>
                      <p className="text-[#475569] mt-2 max-w-lg">{s.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
        </Tilt3D>
      </section>

      {/* ---------- STATS ---------- */}
      <section className="section" data-testid="stats-section">
        <motion.div {...fadeUp}>
          <Eyebrow>{t("stats.eyebrow")}</Eyebrow>
          <h2 className="font-heading font-black text-3xl sm:text-4xl mt-5 text-[#0B1B3A]" data-testid="stats-title">{t("stats.title")}</h2>
        </motion.div>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
          {stats.map((s, i) => (
            <motion.div key={i} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.07 }} className="card-dark p-7 hover:border-[#1D4ED8] transition-colors duration-300" data-testid={`stat-card-${i}`}>
              <p className="font-heading font-black text-4xl sm:text-5xl text-[#1D4ED8]">{s.v}</p>
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#64748B] mt-3">{s.l}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ---------- POPULAR ACADEMY COURSES ---------- */}
      {popular.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-6 pb-20 md:pb-28" data-testid="featured-section">
          <motion.div {...fadeUp} className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <Eyebrow>{t("landing.popular.subtitle")}</Eyebrow>
              <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl mt-5 text-[#0B1B3A]" data-testid="landing-courses-title">
                {t("landing.popular.title")}
              </h2>
            </div>
            <Link data-testid="landing-courses-all" to="/courses" className="btn-outline-light">
              {t("hero.cta2")} <ArrowUpRight className="w-4 h-4" />
            </Link>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-12">
            {popular.map((c, i) => (
              <motion.div key={c.id} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.08 }}>
                <Link data-testid={`landing-course-card-${c.slug}`} to={`/academy/${c.slug}`} className="card-dark block overflow-hidden group hover:border-[#1D4ED8] transition-colors duration-300 h-full">
                  <div className="h-48 overflow-hidden bg-[#EEF4FF]">
                    {c.badge_image && <img src={c.badge_image} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                  </div>
                  <div className="p-6">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="badge-lime">{catLabel(c.category, lang)}</span>
                      {c.is_free && <span className="badge-outline">{t("academy.free")}</span>}
                    </div>
                    <h3 className="font-heading font-black text-xl mt-4 text-[#0B1B3A]">{pick(c, "title", lang)}</h3>
                    <p className="text-sm text-[#64748B] mt-2 line-clamp-2">{pick(c, "subtitle", lang) || pick(c, "description", lang)}</p>
                    <div className="flex items-center justify-between mt-6 pt-4 border-t border-[#E2E8F0]">
                      <span className="text-xs font-bold uppercase tracking-wider text-[#64748B]" data-testid={`landing-course-meta-${c.slug}`}>
                        {c.lessons_count} {t("learn.lessons")} · {c.hours}h
                      </span>
                      <span className="font-heading font-black text-xl text-[#1D4ED8]" data-testid={`landing-course-price-${c.slug}`}>
                        {c.is_free ? t("academy.free") : `${c.price}€`}
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* ---------- ONLINE ACADEMY ---------- */}
      {online.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-6 pb-20 md:pb-28" data-testid="landing-online-section">
          <motion.div {...fadeUp} className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <Eyebrow>{t("landing.online.subtitle")}</Eyebrow>
              <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl mt-5 text-[#0B1B3A]" data-testid="landing-online-title">
                {t("landing.online.title")}
              </h2>
              <p className="text-base text-[#475569] mt-4 max-w-2xl">{t("landing.online.desc")}</p>
            </div>
            <Link data-testid="landing-online-all" to="/courses" className="btn-outline-light">
              {t("nav.academy")} <ArrowUpRight className="w-4 h-4" />
            </Link>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
            {online.map((c, i) => (
              <motion.div key={c.id} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.06 }}>
                <Link data-testid={`landing-online-card-${c.slug}`} to={`/academy/${c.slug}`} className="card-dark block overflow-hidden group hover:border-[#1D4ED8] transition-colors duration-300 h-full">
                  <div className="h-40 overflow-hidden bg-[#0B1B3A] flex items-center justify-center">
                    {c.badge_image ? (
                      <img src={c.badge_image} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <span className="font-heading font-black text-white/90 text-2xl px-6 text-center">{c.title}</span>
                    )}
                  </div>
                  <div className="p-6">
                    <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#1D4ED8]">{catLabel(c.category, lang)}</p>
                    <h3 className="font-heading font-black text-lg mt-3 text-[#0B1B3A]">{pick(c, "title", lang)}</h3>
                    <p className="text-sm text-[#64748B] mt-2 line-clamp-2">{pick(c, "subtitle", lang) || pick(c, "description", lang)}</p>
                    <div className="flex items-center justify-between mt-5 pt-4 border-t border-[#E2E8F0]">
                      <span className="text-xs font-bold uppercase tracking-wider text-[#64748B]">{c.lessons_count} {t("learn.lessons")} · {c.hours}h</span>
                      <span className="font-heading font-black text-xl text-[#0B1B3A]">
                        {c.price_original > c.price && <span className="text-sm text-[#94A3B8] line-through mr-2">{c.price_original}€</span>}
                        {c.price}€
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* ---------- FAQ ---------- */}
      <section className="max-w-7xl mx-auto px-5 sm:px-6 pb-20 md:pb-28" data-testid="science-section">
        <div className="panel-cream p-8 md:p-14 grid lg:grid-cols-[1fr_1.1fr] gap-10 lg:gap-16 items-start">
          <motion.div {...fadeUp}>
            <Eyebrow>{t("science.eyebrow")}</Eyebrow>
            <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl mt-5 text-[#0B1B3A] leading-[1.05]" data-testid="science-title">{t("science.title")}</h2>
            <p className="text-base text-[#475569] mt-6" data-testid="science-body">{t("science.body")}</p>
            <div className="grid grid-cols-2 gap-4 mt-8">
              {[["15+", t("science.s1")], ["250+", t("science.s2")], ["100%", t("science.s3")], [lang === "en" ? "Yearly" : "Ετήσια", t("science.s4")]].map(([v, l], i) => (
                <div key={i} className="card-dark p-5" data-testid={`science-stat-${i}`}>
                  <p className="font-heading font-black text-3xl text-[#1D4ED8]">{v}</p>
                  <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#64748B] mt-2">{l}</p>
                </div>
              ))}
            </div>
          </motion.div>
          <div className="grid sm:grid-cols-2 gap-4">
            {[["r1", FlaskConical], ["r2", Dumbbell], ["r3", HeartPulse], ["r4", BrainCircuit]].map(([k, Icon], i) => (
              <motion.div key={k} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.08 }} className="card-3d p-7" data-testid={`science-role-${i}`}>
                <span className="w-12 h-12 rounded-2xl bg-[#1D4ED8] text-white flex items-center justify-center shadow-[0_6px_14px_rgba(29,78,216,0.35)]"><Icon className="w-6 h-6" /></span>
                <h3 className="font-heading font-black text-lg text-[#0B1B3A] mt-5">{t(`science.${k}`)}</h3>
                <p className="text-sm text-[#475569] mt-2">{t(`science.${k}b`)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="section" data-testid="faq-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <motion.div {...fadeUp} className="lg:col-span-4">
            <Eyebrow>{t("faq.eyebrow")}</Eyebrow>
            <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl mt-5 text-[#0B1B3A] leading-tight" data-testid="faq-title">
              {t("faq.title")}
            </h2>
            <img src={IMG.coaching} alt="Coaching session" className="hidden lg:block w-full h-56 object-cover rounded-[24px] mt-10 border border-[#E2E8F0]" />
          </motion.div>
          <motion.div {...fadeUp} className="lg:col-span-8">
            {faqs.map((f, i) => (
              <FaqItem key={i} q={f.q} a={f.a} index={i} />
            ))}
          </motion.div>
        </div>
      </section>


      {/* ---------- CTA ---------- */}
      <section className="max-w-7xl mx-auto px-5 sm:px-6 pb-20 md:pb-28">
        <motion.div {...fadeUp} className="panel-lime p-8 sm:p-12 lg:p-16 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-6xl leading-[1.03] text-[#0B1B3A]" data-testid="landing-cta-title">
              {t("landing.cta.title")}
            </h2>
            <p className="text-[#475569] text-base md:text-lg mt-5 max-w-xl">{t("landing.cta.subtitle")}</p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <Link data-testid="landing-cta-button" to={user ? "/courses" : "/register"} className="btn-dark text-base !px-9 !py-4">
              {t("hero.cta")} <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </motion.div>
      </section>

      <Footer />
    </div>
  );
}
